import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import services from "../../Data/demo.js";
import { FiChevronRight, FiHome, FiChevronDown } from "react-icons/fi";
import { Helmet } from 'react-helmet';

const ServicesPage = () => {

  const handleScroll = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  // FAQ items
  const faqItems = [
    { 
      question: 'How long does a typical project take?', 
      answer: 'Project timelines vary based on complexity, but most of our projects range from 4-12 weeks. We provide a detailed timeline during our initial consultation.' 
    },
    { 
      question: 'Do you offer ongoing support after project completion?', 
      answer: 'Yes, we offer various support packages to ensure your solution continues to perform optimally. Our team is available for maintenance, updates, and troubleshooting.' 
    },
    { 
      question: 'Can you work with our existing technology stack?', 
      answer: 'Absolutely. We have experience with a wide range of technologies and can integrate with your existing systems or recommend improvements where needed.' 
    },
    { 
      question: 'What makes your approach different from other agencies?', 
      answer: 'We focus on strategic partnerships rather than just project delivery. Our team becomes an extension of yours, ensuring we fully understand your business goals and challenges.' 
    },
    { 
      question: 'How do you handle project communication and updates?', 
      answer: 'We maintain transparent communication through regular meetings, progress reports, and a dedicated project management portal. You\'ll always know where your project stands.' 
    },
    { 
      question: 'What industries do you specialize in?', 
      answer: 'We have experience across multiple industries including e-commerce, healthcare, finance, education, and SaaS. Our solutions are tailored to meet industry-specific requirements and compliance standards.' 
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  // Breadcrumb component with structured data for SEO
  const Breadcrumb = () => {
    return (
      <nav className="mb-8" aria-label="Breadcrumb">
        <ol className="hidden items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-center">
            <Link to="/" onClick={handleScroll} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center">
              <FiHome className="w-4 h-4 mr-1" />
              Home
            </Link>
          </li>
          <li className="flex items-center">
            <FiChevronRight className="w-4 h-4 mx-1" />
            <span className="text-blue-600 dark:text-blue-400 font-medium" aria-current="page">Services</span>
          </li>
        </ol>
        
        {/* Structured data for Google (invisible to users but helps SEO) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": window.location.origin
            }, {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": window.location.href
            }]
          })}
        </script>
      </nav>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Our Services | Mern Digital</title>
        <meta name="description" content="Discover our comprehensive range of digital services designed to transform your business and drive growth in the digital landscape." />
        <link rel="canonical" href="https://merndigital.com/services" />
      </Helmet>
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb Navigation */}
        <Breadcrumb />

        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6"
          >
            <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
            <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">Our Services</span>
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Transform Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Digital Presence</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Comprehensive digital solutions tailored to transform your business and drive growth in the digital landscape
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
        >
          {services.map((service) => (
            <motion.div
              key={service.slug}
              variants={itemVariants}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className="group relative"
            >
              <Link
                to={`/services/${service.slug}`}
                onClick={handleScroll}
                className="block h-full"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 h-full shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700 group-hover:border-blue-200 dark:group-hover:border-blue-600 overflow-hidden">
                  {/* Decorative element */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-bl-full"></div>
                  
                  {/* Icon */}
                  <div className="relative z-10 w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    {service.icon && (
                      <div className="text-2xl text-blue-600 dark:text-blue-400">
                        {service.icon}
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300 relative z-10">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-300 mb-6 relative z-10 line-clamp-3">
                    {service.briefDescription || service.description}
                  </p>

                  {/* CTA */}
                  <div className="flex items-center text-blue-600 dark:text-blue-400 font-semibold group-hover:translate-x-1 transition-transform duration-300 relative z-10">
                    Explore service
                    <FiChevronRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* FAQ Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Find answers to common questions about our services and processes
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {faqItems.map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="flex items-center justify-between w-full p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-shadow text-left"
                  aria-expanded={activeFaq === index}
                >
                  <span className="text-lg font-medium text-gray-900 dark:text-white pr-4">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: activeFaq === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <FiChevronDown className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </motion.div>
                </button>
                
                <AnimatePresence>
                  {activeFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-b-xl border-t border-blue-100 dark:border-blue-800/30">
                        <p className="text-gray-700 dark:text-gray-300">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white">
            {/* Background pattern */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to elevate your business?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                We offer custom solutions tailored to your specific business needs. Let's discuss how we can help you achieve your goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/quotation"
                  onClick={handleScroll}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Get a Custom Quote
                </Link>
                <Link
                  to="/about"
                  onClick={handleScroll}
                  className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white hover:text-blue-600 transition-all"
                >
                  See More About Us
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesPage;